'use client'

import { twMerge } from '@/lib/classname/twMerge'
import type { ButtonAppearanceProps } from '@/ui/buttons/button-styles/buttonAppearance.types'
import { ValidationMessage } from '@/ui/form/validation-message/ValidationMessage.client'
import { Loader } from '@/ui/loaders/loader/Loader'
import { cva } from 'class-variance-authority'
import React, {
  cloneElement,
  useId,
  useMemo,
  type FC,
  type PropsWithChildren,
  type ReactElement,
} from 'react'
import { useFocusRing, useHover } from 'react-aria'
import {
  Input as AriaInput,
  FieldError,
  TextField,
  type InputProps as AriaInputProps,
} from 'react-aria-components'
import type { PrivateUiInputProps, PublicInputProps } from './AbstractInput.client'
import { SharedUILabel } from './SharedUILabel.client'

/**
 * The validation props we allow for a text input component.
 */
type UITextInputAllowedValidators =
  | 'maxLength'
  | 'minLength'
  | 'required'
  | 'pattern'
  | 'asyncValidate'

/**
 * The props to be passed to the shared text input component.
 */
type SharedUITextInputOwnProps = {
  /** The type of the input */
  type?: 'text' | 'email' | 'date' | 'password' | 'number'

  /** The placeholder text to display */
  placeholder?: string

  /** Whether or not the input field is readOnly */
  readOnly?: boolean

  /** To display a button inside the input's right hand side - HAS TO BE A BUTTON */
  rightButton?: ReactElement<ButtonAppearanceProps, FC<ButtonAppearanceProps>>

  /** To display JSX both outside and at the right hand side of the input */
  trailingOutlet?: ReactElement

  /** Classes applied on the wrapper div */
  className?: string

  /** Classes applied on the div carrying the input borders - it should only contain width classes */
  classNameInput?: string

  /** If the input allows for autocomplete */
  autocomplete?: string
}

/**
 * The props to be passed to the shared text input component.
 */
export type SharedUITextInputProps<
  ValidatorsRules extends UITextInputAllowedValidators,
  OwnProps extends object = object,
> = PublicInputProps<SharedUITextInputOwnProps, ValidatorsRules> & OwnProps

const textInputStyles = cva(
  'relative flex h-6 items-center rounded-sm border-px bg-white text-brand-2-700 outline-none outline-2 outline-offset-2 hd:border-1.5',
  {
    variants: {
      hasError: {
        true: 'border-error-fg',
        false: 'border-brand-2-200',
      },
      isFocusVisible: {
        true: 'outline-brand-1-800',
      },
      isFocusedInteractive: {
        true: 'border-brand-2-900',
      },
      isHoveredInteractive: {
        true: 'border-brand-1-900',
      },
      hasRightButton: {
        true: 'pr-0',
      },
      isDisabledOrReadOnly: {
        true: 'border-disabled-bg bg-disabled-bg text-brand-2-700',
      },
    },
  },
)

/**
 * A component which must be re-used to create any type of text-based inputs.
 */
export const SharedUITextInput = React.memo(
  ({
    className,
    label,
    name,
    error,
    isLoading,
    isSubmitting,
    isRequired,
    isDisabled,
    maxLength,
    classNameInput,
    type,
    value,
    onBlur,
    onChange,
    onFocus,
    rightButton,
    trailingOutlet,
    placeholder,
    readOnly,
    autocomplete = 'off',
    children,
    'aria-label': ariaLabel,
    ...props
  }: StrictOmit<PrivateUiInputProps<string, SharedUITextInputOwnProps>, 'onChange' | 'onFocus'> &
    PropsWithChildren &
    AriaInputProps) => {
    const hasError = !!error?.message
    const shouldDisplayError = !isLoading && !isDisabled && hasError

    const inputId = useId()
    const errorId = shouldDisplayError && `${inputId}-error`

    const { isFocusVisible, isFocused, focusProps } = useFocusRing({ within: true })
    const { isHovered, hoverProps } = useHover({})

    const inputProps = useMemo(
      () => ({
        ...props,
        name,
        type,
        id: inputId,
        placeholder,
        maxLength,
        readOnly,
        onFocus,
        onBlur,
        disabled: isDisabled,
        autoComplete: autocomplete ? autocomplete : 'off',
        className: 'h-full max-w-full grow appearance-none bg-clear px-2 outline-none rounded-sm',
        'aria-describedby': errorId || undefined,
        'data-1pignore': true,
      }),
      [
        props,
        onFocus,
        onBlur,
        name,
        type,
        inputId,
        placeholder,
        maxLength,
        readOnly,
        isDisabled,
        autocomplete,
        errorId,
      ],
    )

    const isDisabledOrReadOnly = isDisabled || readOnly
    const isFocusedInteractive = isFocused && !isDisabledOrReadOnly
    const isHoveredInteractive = isHovered && !isDisabledOrReadOnly

    return (
      <TextField
        value={value}
        className={twMerge(
          'min-w-[15rem]',
          className,
          'flex flex-col',
          (isLoading || isSubmitting) && 'pointer-events-none',
        )}
        data-invalid={hasError ? 'true' : undefined}
        isInvalid={shouldDisplayError}
        aria-label={label ? undefined : ariaLabel}
      >
        {label && (
          <SharedUILabel inputId={inputId} label={label} required={isRequired} className="mb-1" />
        )}
        <div className={twMerge(!!trailingOutlet && 'flex flex-row items-center gap-2')}>
          <div
            {...focusProps}
            {...hoverProps}
            className={twMerge(
              textInputStyles({
                hasError: shouldDisplayError,
                isFocusVisible: isFocusVisible && isFocusedInteractive,
                isFocusedInteractive,
                isHoveredInteractive,
                hasRightButton: !!rightButton,
                isDisabledOrReadOnly,
              }),
              classNameInput || 'grow',
            )}
          >
            {/* TODO: reinject inputId (and aria-describedby) onto children via cloneElement —
                regression from d8e79b0 (renderCustomInput → children). Without it, SharedUILabel
                htmlFor is orphaned for custom children (e.g. DatePickerInput / DatePicker). */}
            {children ?? <AriaInput {...inputProps} value={value} onChange={onChange} />}
            {rightButton && (
              <span className={twMerge('grid size-6 place-items-center', isLoading && 'opacity-0')}>
                {typeof rightButton.type === 'string'
                  ? rightButton
                  : cloneElement(rightButton, {
                      variant: 'quaternary',
                      isRound: true,
                      size: 'sm',
                    })}
              </span>
            )}
            {isLoading && (
              <span className="absolute right-0 top-0 grid h-full w-6 place-items-center">
                <Loader className="text-primary" />
              </span>
            )}
          </div>
          {trailingOutlet}
        </div>
        <FieldError>
          <ValidationMessage id={errorId || undefined} status="error">
            {error?.message ?? ''}
          </ValidationMessage>
        </FieldError>
      </TextField>
    )
  },
)

SharedUITextInput.displayName = 'SharedUITextInput'
